import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  AttendanceSummaryDto,
  IAttendanceQuery,
  IAttendanceRecord,
  IAttendanceTimesheetRaw,
  ICreateEmployee,
  ICreateLeaveRequest,
  ICreateMeetingRoom,
  ICreateMeetingSchedule,
  ICreateStationeryPeriod,
  ICreateStationeryProduct,
  ICreateStationeryRequest,
  ICreateStationeryRequestItem,
  IEmployeeByDepartment,
  IEmployeeQuery,
  IEmployeer,
  IEmployeeUpdate,
  IEmployerDetail,
  ILeaveRequest,
  ILeaveRequestQuery,
  IMeetingRoom,
  IMeetingRoomQuery,
  IMeetingSchedule,
  IOvertime,
  IOvertimeCreate,
  IOvertimeRequestQuery,
  IPullAttendanceDto,
  IQuery,
  IQueryStationeryPeriod,
  IQueryStationeryProduct,
  IReviewStationeryRequestItem,
  ISolveAttendanceQuery,
  IStationeryCategory,
  IStationeryPeriod,
  IStationeryPeriodDetail,
  IStationeryProduct,
  IStationeryRequest,
  IStationeryRequestItem,
  IUpdateMeetingRoom,
  IUpdateMeetingSchedule,
  IUpdateStationeryProduct,
  IUpdateStationeryRequestItem,
} from "../models/Hrm";
import { IPaginatedResponse } from "../models/Pagination";
import { hrmService } from "../services/hrmService";
import { notify } from "../utils/toast";
import {
  attendanceQueryKey,
  employeeQueryKey,
  leaveRequestQueryKey,
  meetingQueryKey,
  stationeryQueryKey,
} from "./QueryKey";

import { Status } from "../enum/common";
import { IUserInfo } from "../models/Users";
import { overtimeRequestQueryKey } from "./QueryKey";

export function useGetAttendances(
  query?: IAttendanceQuery,
): UseQueryResult<IPaginatedResponse<IAttendanceRecord>> {
  return useQuery({
    queryKey: [attendanceQueryKey.ATTENDANCES, query],
    queryFn: () => hrmService.getAllAttendance(query),
  });
}

export function useSolveAttendance(): UseMutationResult<
  AttendanceSummaryDto,
  unknown,
  { query: ISolveAttendanceQuery; employeeId: string },
  unknown
> {
  return useMutation<
    AttendanceSummaryDto,
    unknown,
    { query: ISolveAttendanceQuery; employeeId: string }
  >({
    mutationFn: ({ query, employeeId }) => {
      return hrmService.solveAttendance(query, employeeId);
    },

    onError: () => {
      notify.error("Có lỗi xảy ra");
    },
  });
}

export function useGetAttendanceByEmployee(
  employeeId: string,
  query?: IAttendanceQuery,
  enabled?: boolean,
): UseQueryResult<IPaginatedResponse<IAttendanceRecord>> {
  return useQuery({
    queryKey: [attendanceQueryKey.ATTENDANCE_BY_EMPLOYEE, employeeId, query],
    queryFn: () => hrmService.getAttendanceByEmployee(employeeId, query),
    enabled: enabled,
  });
}
export function useGetTimeSheet(
  employeeId: string,
  query?: ISolveAttendanceQuery,
): UseQueryResult<IAttendanceTimesheetRaw> {
  return useQuery({
    queryKey: [attendanceQueryKey.ATTENDANCE_TIMESHEET, employeeId, query],
    queryFn: () => hrmService.getTimeSheetAttendance(query, employeeId),
    enabled: false,
  });
}

export function useGetMyAttendance(
  query?: IAttendanceQuery,
): UseQueryResult<IPaginatedResponse<IAttendanceRecord>> {
  return useQuery({
    queryKey: [attendanceQueryKey.ME, query],
    queryFn: () => hrmService.getMyAttendance(query),
    enabled: !!query,
  });
}

export function useGetDataAttendance(): UseMutationResult<
  IPaginatedResponse<IAttendanceRecord>,
  unknown,
  { query: IAttendanceQuery; pull: IPullAttendanceDto },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { query: IAttendanceQuery; pull: IPullAttendanceDto }) =>
      hrmService.getHikivisionData(body.pull),
    onSuccess: (res, variables) => {
      queryClient.setQueriesData<IPaginatedResponse<IAttendanceRecord>>(
        {
          queryKey: [
            attendanceQueryKey.ATTENDANCE_BY_EMPLOYEE,
            variables.pull.employeeId,
          ],
        },
        res,
      );
    },
    onError: (error) => {
      notify.error("Error");
    },
  });
}

const { OVERTIME_REQUESTS, OVERTIME_REQUESTS_BY_EMPLOYEE } =
  overtimeRequestQueryKey;
export function useGetOvertimeByEmployee(
  employeeId: string,
  query?: IOvertimeRequestQuery,
): UseQueryResult<IPaginatedResponse<IOvertime>> {
  return useQuery({
    queryKey: [OVERTIME_REQUESTS_BY_EMPLOYEE, employeeId, query],
    queryFn: () =>
      hrmService.getAllOvertimeRequestByEmployee(employeeId, query),
    enabled: !!employeeId,
  });
}
export function useGetMyOvertime(
  query?: IOvertimeRequestQuery,
): UseQueryResult<IPaginatedResponse<IOvertime>> {
  return useQuery({
    queryKey: [OVERTIME_REQUESTS_BY_EMPLOYEE, query],
    queryFn: () => hrmService.getMyOvertimeRequest(query),
  });
}
export function useGetOvertimeRequests(
  query?: IOvertimeRequestQuery,
): UseQueryResult<IPaginatedResponse<IOvertime>> {
  return useQuery({
    queryKey: [OVERTIME_REQUESTS, query],
    queryFn: () => hrmService.getAllOvertimeRequest(query),
  });
}
export function useGetOvertimeRequestsByManager(
  query?: IOvertimeRequestQuery,
): UseQueryResult<IPaginatedResponse<IOvertime>> {
  return useQuery({
    queryKey: [OVERTIME_REQUESTS, query],
    queryFn: () => hrmService.getOvertimeRequestByManager(query),
  });
}

export function useCreateOvertimeRequest(): UseMutationResult<
  IOvertime,
  unknown,
  { data: IOvertimeCreate; query: IOvertimeRequestQuery },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      data: IOvertimeCreate;
      query: IOvertimeRequestQuery;
    }) => hrmService.createOvertimeRequest(body.data),
    onSuccess: (data, body) => {
      queryClient.setQueriesData(
        { queryKey: [OVERTIME_REQUESTS_BY_EMPLOYEE, body.query] },
        (oldData: IPaginatedResponse<IOvertime>) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: [data, ...oldData.data],
            pagination: {
              ...oldData.pagination,
              total: oldData.pagination.total + 1,
            },
          };
        },
      );
    },
  });
}

export function useCancelOvertimeRequest(): UseMutationResult<
  IOvertime,
  unknown,
  { id: string },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { id: string }) =>
      hrmService.canceOvertimeRequest(body.id),

    onSuccess: (data, body) => {
      queryClient.setQueriesData<IPaginatedResponse<IOvertime>>(
        {
          queryKey: [OVERTIME_REQUESTS_BY_EMPLOYEE],
        },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.filter((item) => item.id !== body.id),
          };
        },
      );

      queryClient.setQueriesData<IPaginatedResponse<IOvertime>>(
        {
          queryKey: [OVERTIME_REQUESTS],
        },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.filter((item) => item.id !== body.id),
          };
        },
      );
    },
  });
}

const updateOvertimeCache = (
  queryClient: QueryClient,
  id: string,
  data: IOvertime,
) => {
  queryClient.setQueriesData<IPaginatedResponse<IOvertime>>(
    {
      queryKey: [OVERTIME_REQUESTS_BY_EMPLOYEE],
    },
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        data: oldData.data.map((item) =>
          item.id === id
            ? {
                ...item,
                ...data,
              }
            : item,
        ),
      };
    },
  );

  queryClient.setQueriesData<IPaginatedResponse<IOvertime>>(
    {
      queryKey: [OVERTIME_REQUESTS],
    },
    (oldData) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        data: oldData.data.map((item) =>
          item.id === id
            ? {
                ...item,
                ...data,
              }
            : item,
        ),
      };
    },
  );
};

export function useReviewOvertime(): UseMutationResult<
  IOvertime,
  unknown,
  { id: string; isApprove: boolean },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: string; isApprove: boolean }) =>
      hrmService.reviewOvertimeRequest(payload.id, payload.isApprove),

    onSuccess: (data, variables) => {
      updateOvertimeCache(queryClient, variables.id, data);
    },
  });
}

const { LEAVE_REQUESTS } = leaveRequestQueryKey;

export function useGetLeaveRequests(
  query?: ILeaveRequestQuery,
): UseQueryResult<IPaginatedResponse<ILeaveRequest>> {
  return useQuery({
    queryKey: [LEAVE_REQUESTS, query],
    queryFn: () => hrmService.getAllLeaveRequests(query),
  });
}

export function useGetLeaveRequestsByManager(
  query?: ILeaveRequestQuery,
): UseQueryResult<IPaginatedResponse<ILeaveRequest>> {
  return useQuery({
    queryKey: [LEAVE_REQUESTS, query],
    queryFn: () => hrmService.getLeaveRequestsByManager(query),
  });
}

export function useGetLeaveByEmployeeId(
  id: string,
  query?: ILeaveRequestQuery,
): UseQueryResult<IPaginatedResponse<ILeaveRequest>> {
  return useQuery({
    queryKey: [LEAVE_REQUESTS, id, query],
    queryFn: () => hrmService.getLeaveRequestByEmployee(id, query),
    enabled: !!id,
  });
}

export function useGetMyLeaveRequests(body: {
  query?: ILeaveRequestQuery;
}): UseQueryResult<IPaginatedResponse<ILeaveRequest>> {
  return useQuery({
    queryKey: [LEAVE_REQUESTS, body.query],
    queryFn: () => hrmService.getMyLeaveRequest(body.query),
  });
}
export function useCreateLeaveRequest(): UseMutationResult<
  ILeaveRequest,
  unknown,
  {
    data: ICreateLeaveRequest;
    query: ILeaveRequestQuery;
    id: string;
  },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      data: ICreateLeaveRequest;
      query: ILeaveRequestQuery;
      id: string;
    }) => hrmService.createLeaveRequest(body.data),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<IPaginatedResponse<ILeaveRequest>>(
        { queryKey: [LEAVE_REQUESTS] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: [data, ...oldData.data],
            pagination: {
              ...oldData.pagination,
              total: oldData.pagination.total + 1,
            },
          };
        },
      );
    },
  });
}

export function useCancelLeaveRequest(): UseMutationResult<
  ILeaveRequest,
  unknown,
  { id: string },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { id: string }) =>
      hrmService.cancelLeaveRequest(body.id),
    onSuccess: (_, body) => {
      queryClient.setQueriesData(
        { queryKey: [LEAVE_REQUESTS] },
        (oldData: IPaginatedResponse<ILeaveRequest>) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.filter(
              (item: ILeaveRequest) => item.id !== body.id,
            ),
          };
        },
      );
    },
  });
}
export function useReviewLeaveRequest(): UseMutationResult<
  ILeaveRequest,
  unknown,
  {
    id: string;
    isApprove: boolean;
  },
  unknown
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { id: string; isApprove: boolean }) =>
      hrmService.reviewLeaveRequest(payload.id, payload.isApprove),

    onSuccess: (updatedLeaveRequest, variables) => {
      queryClient.setQueriesData(
        {
          queryKey: [LEAVE_REQUESTS],
          exact: false,
        },
        (oldData: IPaginatedResponse<ILeaveRequest>) => {
          if (!oldData?.data || !Array.isArray(oldData.data)) {
            return oldData;
          }

          return {
            ...oldData,
            data: oldData.data.map((item: ILeaveRequest) =>
              item.id === variables.id
                ? {
                    ...item,
                    ...updatedLeaveRequest,
                  }
                : item,
            ),
          };
        },
      );
    },
  });
}

const { EMPLOYEES_WITH_FILTERS, EMPLOYEE_BY_ID, EMPLOYEE_WITHOUT_USER } =
  employeeQueryKey;

export function useGetEmployees(
  query?: IEmployeeQuery,
): UseQueryResult<IPaginatedResponse<IEmployeer>> {
  return useQuery({
    queryKey: [EMPLOYEES_WITH_FILTERS, query],
    queryFn: () => hrmService.getAllEmployee(query),
  });
}
export function useGetUsersWithoutEmployee(
  query?: IQuery,
): UseQueryResult<IPaginatedResponse<IUserInfo>> {
  return useQuery({
    queryKey: [EMPLOYEE_WITHOUT_USER, query],
    queryFn: () => hrmService.getUsersWithoutEmployee(query),
  });
}

export function useGetEmployee(
  id: string,
  query?: IEmployeeQuery,
): UseQueryResult<IEmployerDetail> {
  return useQuery({
    queryKey: [EMPLOYEE_BY_ID, id],
    queryFn: () => hrmService.getEmployeeById(id, query),
    enabled: !!id,
  });
}
export function useGetEmployeeByDepartment(
  departmentCode: string,
): UseQueryResult<IEmployeeByDepartment[]> {
  return useQuery({
    queryKey: [EMPLOYEES_WITH_FILTERS, departmentCode],
    queryFn: () => hrmService.getEmployeeByDepartment(departmentCode),
    enabled: !!departmentCode,
  });
}
export function useGetMyEmployee(): UseQueryResult<IEmployerDetail> {
  return useQuery({
    queryKey: [EMPLOYEE_BY_ID, "me"],
    queryFn: () => hrmService.getMyEmployeeInfo(),
  });
}

export function useCreateEmployee(): UseMutationResult<
  IEmployeer,
  unknown,
  {
    query?: IEmployeeQuery;
    data: Partial<ICreateEmployee>;
  },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      query?: IEmployeeQuery;
      data: Partial<ICreateEmployee>;
    }) => hrmService.createEmployee(body.data),
    onSuccess: (data, variants) => {
      return queryClient.setQueriesData<IPaginatedResponse<IEmployeer>>(
        { queryKey: [EMPLOYEES_WITH_FILTERS] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: [data, ...oldData.data],
          };
        },
      );
    },
  });
}

export function useUpdateEmployee(): UseMutationResult<
  IEmployeer,
  unknown,
  { employeeData: IEmployeeUpdate; id: string },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeData,
      id,
    }: {
      id: string;
      employeeData: IEmployeeUpdate;
    }) => hrmService.updateEmployee(id, employeeData),
    onSuccess: (_, { id, employeeData }) => {
      queryClient.setQueriesData(
        { queryKey: [EMPLOYEE_BY_ID, id] },
        (old: IEmployeer) => {
          if (!old) return old;
          return {
            ...old,
            ...employeeData,
          };
        },
      );
      queryClient.setQueriesData(
        { queryKey: [EMPLOYEES_WITH_FILTERS] },
        (oldData: IPaginatedResponse<IEmployeer>) => {
          if (!oldData || !oldData.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item: IEmployeer) =>
              item.id === id ? { ...item, ...employeeData } : item,
            ),
          };
        },
      );
    },
  });
}

const {
  STATIONERY_PRODUCT,
  STATIONERY_PERIOD,
  STATIONERY_REQUEST,
  STATIONERY_CATEGORY,
} = stationeryQueryKey;

export const useGetAllStationeryProduct = (
  params?: IQueryStationeryProduct,
): UseQueryResult<IPaginatedResponse<IStationeryProduct>> => {
  return useQuery({
    queryKey: [STATIONERY_PRODUCT, params],
    queryFn: () => hrmService.getAllStationeryProducts(params),
  });
};

export const useStationeryProduct = (
  id: number,
): UseQueryResult<IStationeryProduct> => {
  return useQuery({
    queryKey: [STATIONERY_PRODUCT, id],
    queryFn: () => hrmService.getOneStationeryProduct(id),
    enabled: !!id,
  });
};

export const useCreateStationeryProduct = (): UseMutationResult<
  IStationeryProduct,
  unknown,
  ICreateStationeryProduct,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICreateStationeryProduct) =>
      hrmService.createStationeryProduct(payload),
    onSuccess: (data) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryProduct>>(
        { queryKey: [STATIONERY_PRODUCT] },
        (oldData) => {
          if (!oldData || !oldData.data) return oldData;
          return {
            ...oldData,
            data: [data, ...oldData.data],
            pagination: {
              ...oldData.pagination,
              total: oldData.pagination.total + 1,
            },
          };
        },
      );
    },
  });
};

export const useUpdateStationeryProduct = (): UseMutationResult<
  IStationeryProduct,
  unknown,
  { id: number; payload: IUpdateStationeryProduct },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: IUpdateStationeryProduct;
    }) => hrmService.updateStationeryProduct(id, payload),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryProduct>>(
        { queryKey: [STATIONERY_PRODUCT] },
        (oldData) => {
          if (!oldData || !oldData.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === variables.id ? data : item,
            ),
          };
        },
      );
      queryClient.setQueryData([STATIONERY_PRODUCT, variables.id], data);
    },
  });
};

export const useDeleteStationeryProduct = (): UseMutationResult<
  IStationeryProduct,
  unknown,
  number,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrmService.removeStationeryProduct(id),
    onSuccess: (_, id) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryProduct>>(
        { queryKey: [STATIONERY_PRODUCT] },
        (oldData) => {
          if (!oldData || !oldData.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.filter((item) => item.id !== id),
            pagination: {
              ...oldData.pagination,
              total: Math.max(0, oldData.pagination.total - 1),
            },
          };
        },
      );
    },
  });
};

export const useImportStationeryProduct = (): UseMutationResult<
  IStationeryProduct[],
  unknown,
  FormData,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: FormData) =>
      hrmService.importStationeryProduct(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [STATIONERY_PRODUCT] });
    },
  });
};

export const useGetAllStationeryPeriods = (
  params?: IQueryStationeryPeriod,
): UseQueryResult<IPaginatedResponse<IStationeryPeriod>> => {
  return useQuery({
    queryKey: [STATIONERY_PERIOD, params],
    queryFn: () => hrmService.getAllStationeryPeriods(params),
  });
};

export const useGetOneStationeryPeriod = (
  id: number,
): UseQueryResult<IStationeryPeriodDetail> => {
  return useQuery({
    queryKey: [STATIONERY_PERIOD, id],
    queryFn: () => hrmService.getOneStationeryPeriod(id),
    enabled: !!id,
  });
};

export const useCreateStationeryPeriod = (): UseMutationResult<
  IStationeryPeriod,
  unknown,
  ICreateStationeryPeriod,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICreateStationeryPeriod) =>
      hrmService.createStationeryPeriod(payload),
    onSuccess: (data) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryPeriod>>(
        { queryKey: [STATIONERY_PERIOD], exact: false },
        (oldData) => {
          if (!oldData?.data || !Array.isArray(oldData.data)) return oldData;
          return {
            ...oldData,
            data: [data, ...oldData.data],
            pagination: {
              ...oldData.pagination,
              total: oldData.pagination.total + 1,
            },
          };
        },
      );
    },
  });
};

export const useCloseStationeryPeriod = (): UseMutationResult<
  IStationeryPeriod,
  unknown,
  number,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrmService.closeStationeryPeriod(id),
    onSuccess: (data, id) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryPeriod>>(
        { queryKey: [STATIONERY_PERIOD], exact: false },
        (oldData) => {
          if (!oldData?.data || !Array.isArray(oldData.data)) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) => (item.id === id ? data : item)),
          };
        },
      );
      queryClient.setQueryData([STATIONERY_PERIOD, id], data);
    },
  });
};

export const useOpenStationeryPeriod = (): UseMutationResult<
  IStationeryPeriod,
  unknown,
  number,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrmService.openStationeryPeriod(id),
    onSuccess: (data, id) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryPeriod>>(
        { queryKey: [STATIONERY_PERIOD], exact: false },
        (oldData) => {
          if (!oldData?.data || !Array.isArray(oldData.data)) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) => (item.id === id ? data : item)),
          };
        },
      );
      queryClient.setQueryData([STATIONERY_PERIOD, id], data);
    },
  });
};

export const useReviewStationeryPeriod = (): UseMutationResult<
  IStationeryPeriod,
  unknown,
  { id: number; isApprove: boolean },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isApprove }: { id: number; isApprove: boolean }) =>
      hrmService.reviewStationeryPeriod(id, isApprove),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryPeriod>>(
        { queryKey: [STATIONERY_PERIOD], exact: false },
        (oldData) => {
          if (!oldData?.data || !Array.isArray(oldData.data)) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === variables.id ? data : item,
            ),
          };
        },
      );
      queryClient.setQueryData([STATIONERY_PERIOD, variables.id], data);
    },
  });
};
export const useUpdateStationeryPeriod = (): UseMutationResult<
  IStationeryPeriod,
  unknown,
  { id: number; data: Partial<IStationeryPeriod> },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<IStationeryPeriod>;
    }) => hrmService.updateStationeryPeriod(id, data),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryPeriod>>(
        { queryKey: [STATIONERY_PERIOD], exact: false },
        (oldData) => {
          if (!oldData?.data || !Array.isArray(oldData.data)) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === variables.id ? data : item,
            ),
          };
        },
      );
      queryClient.setQueryData([STATIONERY_PERIOD, variables.id], data);
    },
  });
};

export const useOneStationeryRequest = (
  id: number,
): UseQueryResult<IStationeryRequest> => {
  return useQuery({
    queryKey: [STATIONERY_REQUEST, id],
    queryFn: () => hrmService.getOneStationeryRequest(id),
    enabled: !!id,
  });
};
export const useGetMyStationeryRequest = (
  id: number,
): UseQueryResult<IStationeryRequest> => {
  return useQuery({
    queryKey: [STATIONERY_REQUEST, id],
    queryFn: () => hrmService.getMyStationeryRequest(id),
    enabled: !!id,
  });
};
export const useCreateStationeryRequest = (): UseMutationResult<
  IStationeryRequest,
  unknown,
  ICreateStationeryRequest,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICreateStationeryRequest) =>
      hrmService.createStationeryRequest(payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<IStationeryPeriodDetail>(
        [STATIONERY_PERIOD, variables.periodId],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            requests: [...(oldData.requests || []), data],
          };
        },
      );
    },
  });
};
export const useUpdateNoteStationeryRequest = (): UseMutationResult<
  IStationeryRequestItem[],
  unknown,
  { id: number; note: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: number; note: string }) =>
      hrmService.updateStationeryRequestNote(payload.id, payload.note),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<IStationeryRequest[]>(
        {
          queryKey: [STATIONERY_REQUEST],
        },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            note: variables.note,
          };
        },
      );
    },
  });
};
export const useReviewBulkStationeryRequest = (): UseMutationResult<
  IStationeryRequest,
  unknown,
  { requestId: number; items: IReviewStationeryRequestItem[] },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      requestId: number;
      items: IReviewStationeryRequestItem[];
    }) =>
      hrmService.reviewBulkStationeryRequest(payload.requestId, payload.items),

    onSuccess: (data, variables) => {
      queryClient.setQueriesData<IStationeryPeriodDetail>(
        { queryKey: [STATIONERY_REQUEST] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            requests: oldData.requests?.map((request) =>
              request.id === variables.requestId ? data : request,
            ),
          };
        },
      );
    },
  });
};
export const useCreateStationeryRequestItems = (): UseMutationResult<
  IStationeryRequestItem[],
  unknown,
  {
    requestId: string | number;
    payload: Partial<ICreateStationeryRequestItem>[];
  },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      requestId,
      payload,
    }: {
      requestId: string | number;
      payload: Partial<ICreateStationeryRequestItem>[];
    }) => hrmService.createStationeryRequestItemBulk(requestId, payload),
    onSuccess: (newItems, variables) => {
      queryClient.setQueriesData(
        { queryKey: [STATIONERY_REQUEST] },
        (oldData: IStationeryRequest | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: [...oldData.items, ...newItems],
          };
        },
      );
    },
  });
};

export const useUpdateStationeryRequestItems = (): UseMutationResult<
  IStationeryRequestItem[],
  unknown,
  IUpdateStationeryRequestItem[],
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IUpdateStationeryRequestItem[]) =>
      hrmService.updateStationeryRequestItemBulk(payload),
    onSuccess: (updatedItems) => {
      queryClient.setQueriesData(
        { queryKey: [STATIONERY_REQUEST] },
        (oldData: IStationeryRequest | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            items: oldData.items.map((item) => {
              const updated = updatedItems.find((u) => u.id === item.id);
              return updated ?? item;
            }),
          };
        },
      );
    },
  });
};

export const useDeleteStationeryRequestItem = (): UseMutationResult<
  IStationeryRequestItem[],
  unknown,
  number[],
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: number[]) =>
      hrmService.removeBulkStationeryRequestItem(ids),
    onSuccess: (_, deletedIds) => {
      queryClient.setQueriesData(
        { queryKey: [STATIONERY_REQUEST] },
        (oldData: IStationeryRequest | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            items: oldData.items.filter(
              (item) => !deletedIds.includes(item.id),
            ),
          };
        },
      );
    },
  });
};

export const useGetCategories = (filters: IQuery) => {
  return useQuery({
    queryKey: [STATIONERY_CATEGORY, filters],
    queryFn: () => hrmService.getCategories(filters),
  });
};

export const useDeleteStationeryCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => hrmService.deleteCategory(id),
    onSuccess: () => {
      notify.success("Xóa thành công!");
      queryClient.invalidateQueries({ queryKey: [STATIONERY_CATEGORY] });
    },
    onError: () => notify.error("Xóa thất bại!"),
  });
};

export const useCreateStationeryCategory = (): UseMutationResult<
  IStationeryCategory,
  unknown,
  { name: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) => hrmService.createCategory(data),
    onSuccess: (data, variants) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryCategory>>(
        { queryKey: [STATIONERY_CATEGORY], exact: false },
        (oldData) => {
          if (!oldData?.data || !Array.isArray(oldData.data)) return oldData;
          return {
            ...oldData,
            data: [data, ...oldData.data],
            pagination: {
              ...oldData.pagination,
              total: oldData.pagination.total + 1,
            },
          };
        },
      );
    },
  });
};

export const useUpdateStationeryCategory = (): UseMutationResult<
  IStationeryCategory,
  unknown,
  { id: number; name: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; name: string }) =>
      hrmService.updateCategory({ id, ...data }),
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<IPaginatedResponse<IStationeryCategory>>(
        { queryKey: [STATIONERY_CATEGORY], exact: false },
        (oldData) => {
          if (!oldData?.data || !Array.isArray(oldData.data)) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === variables.id ? data : item,
            ),
          };
        },
      );
    },
  });
};
export const useGetAllMeetingSchedule = (
  date?: string,
): UseQueryResult<IMeetingSchedule[]> => {
  return useQuery({
    queryKey: [meetingQueryKey.GET_ALL_MEETING_SCHEDULE, date],
    queryFn: () => hrmService.getAllMeetingSchedule(date),
  });
};
export const useGetMyMeetingSchedule = (
  date?: string,
): UseQueryResult<IMeetingSchedule[]> => {
  return useQuery({
    queryKey: [meetingQueryKey.GET_MY_MEETING_SCHEDULE, date],
    queryFn: () => hrmService.getMyMeetingSchedule(date),
  });
};

export const useGetMeetingScheduleById = (
  id: string,
): UseQueryResult<IMeetingSchedule> => {
  return useQuery({
    queryKey: [meetingQueryKey.GET_MEETING_SCHEDULE_BY_ID, id],
    queryFn: () => hrmService.getMeetingScheduleById(id),
    enabled: !!id,
  });
};

export const useCreateMeetingSchedule = (): UseMutationResult<
  IMeetingSchedule,
  unknown,
  ICreateMeetingSchedule,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateMeetingSchedule) =>
      hrmService.createMeetingSchedule(data),
    onSuccess: (res) => {
      const newData = res;
      const updateData = (oldData: IPaginatedResponse<IMeetingSchedule>) => {
        if (!oldData) return oldData;
        const isArray = Array.isArray(oldData);
        const dataArray = isArray ? oldData : oldData?.data;

        if (Array.isArray(dataArray)) {
          const newDataArray = [newData, ...dataArray];
          return isArray ? newDataArray : { ...oldData, data: newDataArray };
        }
        return oldData;
      };
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_ALL_MEETING_SCHEDULE] },
        updateData,
      );
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_MY_MEETING_SCHEDULE] },
        updateData,
      );
    },
  });
};
export const useUpdateMeetingSchedule = (): UseMutationResult<
  IMeetingSchedule,
  unknown,
  { id: number | string; data: IUpdateMeetingSchedule },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number | string;
      data: IUpdateMeetingSchedule;
    }) => hrmService.updateMeetingSchedule(id, data),
    onSuccess: (res, variables) => {
      const updatedData = res;
      queryClient.setQueryData(
        [meetingQueryKey.GET_MEETING_SCHEDULE_BY_ID, variables.id],
        res,
      );

      const updateData = (oldData: IPaginatedResponse<IMeetingSchedule>) => {
        if (!oldData) return oldData;
        const isArray = Array.isArray(oldData);
        const dataArray = isArray ? oldData : oldData?.data;

        if (Array.isArray(dataArray)) {
          const newDataArray = dataArray.map((item: IMeetingSchedule) =>
            item.id === variables.id ? { ...item, ...updatedData } : item,
          );
          return isArray ? newDataArray : { ...oldData, data: newDataArray };
        }
        return oldData;
      };

      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_ALL_MEETING_SCHEDULE] },
        updateData,
      );
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_MY_MEETING_SCHEDULE] },
        updateData,
      );
    },
  });
};
export const useReviewMeetingSchedule = (): UseMutationResult<
  IMeetingSchedule,
  unknown,
  { id: number | string; data: Status },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: Status }) =>
      hrmService.reviewMeetingSchedule(id, data),
    onSuccess: (res, variables) => {
      const updatedData = res;
      queryClient.setQueryData(
        [meetingQueryKey.GET_MEETING_SCHEDULE_BY_ID, variables.id],
        res,
      );
      const updateData = (oldData: IPaginatedResponse<IMeetingSchedule>) => {
        if (!oldData) return oldData;
        const isArray = Array.isArray(oldData);
        const dataArray = isArray ? oldData : oldData?.data;
        if (Array.isArray(dataArray)) {
          const newDataArray = dataArray.map((item: IMeetingSchedule) =>
            item.id === variables.id ? { ...item, ...updatedData } : item,
          );
          return isArray ? newDataArray : { ...oldData, data: newDataArray };
        }
        return oldData;
      };
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_ALL_MEETING_SCHEDULE] },
        updateData,
      );
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_MY_MEETING_SCHEDULE] },
        updateData,
      );
    },
  });
};

export const useRemoveMeetingSchedule = (): UseMutationResult<
  IMeetingSchedule,
  unknown,
  number | string,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => hrmService.removeMeetingSchedule(id),
    onSuccess: (_, deletedId) => {
      const updateData = (oldData: IPaginatedResponse<IMeetingSchedule>) => {
        if (!oldData) return oldData;
        const isArray = Array.isArray(oldData);
        const dataArray = isArray ? oldData : oldData?.data;
        if (Array.isArray(dataArray)) {
          const newDataArray = dataArray.filter(
            (item: IMeetingSchedule) => item.id !== deletedId,
          );
          return isArray ? newDataArray : { ...oldData, data: newDataArray };
        }
        return oldData;
      };
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_ALL_MEETING_SCHEDULE] },
        updateData,
      );
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_MY_MEETING_SCHEDULE] },
        updateData,
      );
    },
  });
};

export const useGetAllMeetingRoom = (
  params?: IMeetingRoomQuery,
): UseQueryResult<IPaginatedResponse<IMeetingRoom>> => {
  return useQuery({
    queryKey: [meetingQueryKey.GET_ALL_MEETING_ROOM, params],
    queryFn: () => hrmService.getAllMeetingRoom(params),
  });
};

export const useCreateMeetingRoom = (): UseMutationResult<
  IMeetingRoom,
  unknown,
  ICreateMeetingRoom,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateMeetingRoom) =>
      hrmService.createMeetingRoom(data),
    onSuccess: (res) => {
      const newData = res;
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_ALL_MEETING_ROOM] },
        (oldData: IPaginatedResponse<IMeetingRoom>) => {
          if (!oldData) return oldData;
          const isArray = Array.isArray(oldData);
          const dataArray = isArray ? oldData : oldData?.data;
          if (Array.isArray(dataArray)) {
            const newDataArray = [newData, ...dataArray];
            return isArray ? newDataArray : { ...oldData, data: newDataArray };
          }
          return oldData;
        },
      );
    },
  });
};

export const useUpdateMeetingRoom = (): UseMutationResult<
  IUpdateMeetingRoom,
  unknown,
  { id: number | string; data: IUpdateMeetingRoom },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number | string;
      data: IUpdateMeetingRoom;
    }) => hrmService.updateMeetingRoom(id, data),
    onSuccess: (res, variables) => {
      const updatedData = res;
      queryClient.setQueryData(
        [meetingQueryKey.GET_MEETING_ROOM_BY_ID, variables.id],
        res,
      );
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_ALL_MEETING_ROOM] },
        (oldData: IPaginatedResponse<IMeetingRoom>) => {
          if (!oldData) return oldData;
          const dataArray = oldData?.data;
          if (Array.isArray(dataArray)) {
            const newDataArray = dataArray.map((item: IMeetingRoom) =>
              item.id === variables.id ? { ...item, ...updatedData } : item,
            );
            return { ...oldData, data: newDataArray };
          }
          return oldData;
        },
      );
    },
  });
};

export const useRemoveMeetingRoom = (): UseMutationResult<
  IMeetingRoom,
  unknown,
  number | string,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => hrmService.removeMeetingRoom(id),
    onSuccess: (_, deletedId) => {
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_ALL_MEETING_ROOM] },
        (oldData: IPaginatedResponse<IMeetingRoom>) => {
          if (!oldData) return oldData;
          const isArray = Array.isArray(oldData);
          const dataArray = isArray ? oldData : oldData?.data;
          if (Array.isArray(dataArray)) {
            const newDataArray = dataArray.filter(
              (item: IMeetingRoom) => item.id !== deletedId,
            );
            return isArray ? newDataArray : { ...oldData, data: newDataArray };
          }
          return oldData;
        },
      );

      const updateSchedules = (oldData: IPaginatedResponse<IMeetingRoom>) => {
        if (!oldData) return oldData;
        const isArray = Array.isArray(oldData);
        const dataArray = isArray ? oldData : oldData?.data;
        if (Array.isArray(dataArray)) {
          const newDataArray = dataArray.filter(
            (item: IMeetingSchedule) => item.meetingRooms?.id !== deletedId,
          );
          return isArray ? newDataArray : { ...oldData, data: newDataArray };
        }
        return oldData;
      };

      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_ALL_MEETING_SCHEDULE] },
        updateSchedules,
      );
      queryClient.setQueriesData(
        { queryKey: [meetingQueryKey.GET_MY_MEETING_SCHEDULE] },
        updateSchedules,
      );
    },
  });
};
